import { Injectable } from '@angular/core';
import { mapKey } from 'core/rx-js';
import { SnackbarService } from 'core/services';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs';
import { ITask } from '../../dto-map/i-task';
import { ProcessStepperService } from '../../process-stepper.service';
import { RemoteActivityService } from '../../remotes/remote-activity.service';
import { RemoteTaskService } from '../../remotes/remote-task.service';



@Injectable()
export class SelectTasksStepService {
  private readonly _destroy = new Subject<void>()

  readonly activitiesSubject = this._remoteActivityService.activitiesSubject;
  readonly tasksSubject = this._remoteTaskService.localCollectionStore.stateSubject;

  readonly signaturesObs = this.tasksSubject.pipe(
    map(tasks => tasks?.filter(({ type }) => type === 'SIGNATURE').map((task) => task as ITask<'SIGNATURE'>))
  )

  readonly hangingSignaturePositionSubject = new BehaviorSubject<{ x: number, y: number }>(null)

  readonly everyActorHasATaskObs = combineLatest([this.activitiesSubject, this.tasksSubject]).pipe(
    map(([activites, tasks]) => {
      const activitiyIds = activites.map(({ id }) => id);

      const activityIdsFromTask: Set<string> = tasks.reduce<Set<string>>((activityIdsFromTask, task) => {
        activityIdsFromTask.add(task.activityId)
        return activityIdsFromTask
      },
        new Set<string>()
      )

      return activitiyIds.reduce<boolean>((allHaveATask, current) => allHaveATask && activityIdsFromTask.has(current), true)
    }),
    distinctUntilChanged(),
  )

  readonly everySignatureTaskHasAllowedSignatureTypesObs = this.tasksSubject.pipe(
    filter(Boolean),
    map((tasks) => tasks.filter(task => task.type === 'SIGNATURE') as ITask<'SIGNATURE'>[]),
    map((tasks) => !tasks.find((task => !task.configuration?.allowedSignatureTypes?.length))),
    startWith(false)
  )


  readonly stepValidObs = combineLatest([this.everyActorHasATaskObs, this.everySignatureTaskHasAllowedSignatureTypesObs]).pipe(map(arr => arr.filter(Boolean).length === arr.length));

  readonly triedNextObs = this._processStepperService.triedNextSubject.pipe(mapKey('setSignatures'));
  readonly currentStepObs = this._processStepperService.currentStepSubject.pipe(map(step => step === 'setSignatures'));


  constructor(
    private readonly _remoteTaskService: RemoteTaskService,
    private readonly _remoteActivityService: RemoteActivityService,
    private readonly _processStepperService: ProcessStepperService,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,

  ) {


    this.triedNextObs
      .pipe(
        filter(Boolean),
        switchMap(() => combineLatest([this.everyActorHasATaskObs, this.everySignatureTaskHasAllowedSignatureTypesObs, this.currentStepObs])
          .pipe(
            takeUntil(this._destroy),
            filter(([everyActorHasATask, everySignatureTaskHasAllowedSignatureTypes, currentStep]) => !(everyActorHasATask && everySignatureTaskHasAllowedSignatureTypes) && currentStep),
            map(([everyActorHasATask]) => !everyActorHasATask ? 'everyActorHasATask' as const : 'everySignatureTaskHasAllowedSignatureTypes' as const),
            distinctUntilChanged()
          )
        ))
      .subscribe((errorType) => {

        if (errorType === 'everyActorHasATask') {
          this._snackbarService.open({ message: 'ss-process-wizard.select-tasks.every-actor-need-task', type: 'error', }, { duration: 10 * 1000 })
          return;
        }

        if (errorType === 'everySignatureTaskHasAllowedSignatureTypes') {
          this._snackbarService.open({ message: 'ss-process-wizard.select-tasks.every-signature-needs-config', type: 'error', }, { duration: 10 * 1000 })
          return;
        }

      })

  }



  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }


}
